import {
  ActionIcon,
  Anchor,
  AppShell,
  Avatar,
  Burger,
  Flex,
  Group,
  Menu,
  Modal,
  NavLink,
  Text,
} from "@mantine/core";
import VentConfigTab from "./VentConfigTab";
import { Link, useLocation, useRoute } from "wouter";
import GuestsTab from "./GuestsTab";
import GuestTab from "./GuestTab";
import { useEffect, useState } from "react";
import VentsTab from "./VentsTab";
import ActivitiesTab from "./ActivitiesTab";
import ActivityTab from "./ActivityTab";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import VentHomeTab from "./VentHomeTab";
import { useAuth } from "../authentication/AuthProvider";
import { formatInitials } from "../shared/utility";
import { useGetVentDisplayInfoQuery } from "../slices/api/ventsApi";
import VentsGuestTab from "./VentsGuestTab";
import Password from "./Password";
import {
  IconConfigureEvent,
  IconCustomField,
  IconEmailConfiguration,
  IconEmailTemplate,
  IconEventHome,
  IconEventsAsGuest,
  IconEventsAsHost,
  IconGuests,
  IconPassword,
  IconRsvp,
  IconActivity,
  IconActivities,
  IconUserSettings,
} from "../shared/components/Icons";
import EmailTemplatesTab from "./EmailTemplatesTab";
import EmailTemplateTab from "./EmailTemplateTab";
import { Logo } from "../shared/components/Logo";
import { useGetUsersGuestIdForVentQuery } from "../slices/api/guestsApi";
import CustomFieldsTab from "./CustomFieldsTab";
import CustomTextFieldTab from "./CustomTextFieldTab";
import CustomOptionsFieldTab from "./CustomOptionsFieldTab";
import UsersTab from "./UsersTab";
import UserTab from "./UserTab";
import UtilitiesTab from "./UtilitiesTab";
import UserAttributesTab from "./UserAttributesTab";
import { getLinkToSettings } from "../shared/links";

/*
Keep tabs open unless the basis for those tabs changes (e.g., when ventId changes cannot keep a guest tab open for a diffrect event).
*/

export let setConsoleIds: (ids: {
  ventId?: string | null;
  guestId?: string | null;
  activityId?: string | null;
  customFieldId?: string | null;
  emailTemplateId?: string | null;
}) => void;

const Console = () => {
  const authContext = useAuth();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [opened, { toggle, close: closeNavbar }] = useDisclosure();
  const [openedPassword, { open: openPassword, close: closePassword }] =
    useDisclosure(false);
  const closeIfMobile = () => {
    if (isMobile) closeNavbar();
  };

  const [currentVentId, setCurrentVentId] = useState<string | undefined>();
  const [currentGuestId, setCurrentGuestId] = useState<string | undefined>();
  const [currentActivityId, setCurrentActivityId] = useState<
    string | undefined
  >();
  const [currentCustomFieldId, setCurrentCustomFieldId] = useState<
    string | undefined
  >();
  const [currentEmailTemplateId, setCurrentEmailTemplateId] = useState<
    string | undefined
  >();
  const [currentUserId, setCurrentUserId] = useState<string | undefined>();
  // Omitting an ID means it is not being set.
  // Setting it to null means to clear the current value.
  const setIds = (ids: {
    ventId?: string | null;
    guestId?: string | null;
    activityId?: string | null;
    customFieldId?: string | null;
    emailTemplateId?: string | null;
    userId?: string | null;
  }) => {
    if (ids.ventId !== undefined && ids.ventId !== currentVentId) {
      setCurrentVentId(ids.ventId === null ? undefined : ids.ventId);
      setCurrentGuestId(ids.guestId === null ? undefined : ids.guestId);
      setCurrentActivityId(
        ids.activityId === null ? undefined : ids.activityId,
      );
      setCurrentCustomFieldId(
        ids.customFieldId === null ? undefined : ids.customFieldId,
      );
      setCurrentEmailTemplateId(
        ids.emailTemplateId === null ? undefined : ids.emailTemplateId,
      );
      return;
    }
    if (ids.guestId !== undefined && ids.guestId !== currentGuestId) {
      setCurrentGuestId(ids.guestId === null ? undefined : ids.guestId);
    }
    if (ids.activityId !== undefined && ids.activityId !== currentActivityId) {
      setCurrentActivityId(
        ids.activityId === null ? undefined : ids.activityId,
      );
    }
    if (
      ids.customFieldId !== undefined &&
      ids.customFieldId !== currentCustomFieldId
    ) {
      setCurrentCustomFieldId(
        ids.customFieldId === null ? undefined : ids.customFieldId,
      );
    }
    if (
      ids.emailTemplateId !== undefined &&
      ids.emailTemplateId !== currentEmailTemplateId
    ) {
      setCurrentEmailTemplateId(
        ids.emailTemplateId === null ? undefined : ids.emailTemplateId,
      );
    }
    if (ids.userId !== undefined && ids.userId !== currentUserId) {
      setCurrentUserId(ids.userId === null ? undefined : ids.userId);
    }
  };

  setConsoleIds = setIds;

  let tab = "hosted";
  const [matchGuest, paramsGuest] = useRoute(
    "/console/:tab/event/:ventId/guest/:guestId",
  );
  const [matchVent, paramsVent] = useRoute("/console/:tab/event/:ventId");
  const [matchActivity, paramsActivity] = useRoute(
    "/console/:tab/event/:ventId/activity/:activityId",
  );
  const [matchCustomField, paramsCustomField] = useRoute(
    "/console/:tab/event/:ventId/customfield/:customFieldId",
  );
  const [matchEmailTemplate, paramsEmailTemplate] = useRoute(
    "/console/:tab/event/:ventId/emailTemplate/:emailTemplateId",
  );
  const [matchUser, paramsUser] = useRoute("/console/:tab/userId/:userId");
  const [matchTab, paramsTab] = useRoute("/console/:tab");

  if (matchGuest) {
    setIds({ ventId: paramsGuest.ventId, guestId: paramsGuest.guestId });
    tab = paramsGuest.tab;
  } else if (matchVent) {
    setIds({ ventId: paramsVent.ventId });
    tab = paramsVent.tab;
  } else if (matchTab) {
    tab = paramsTab.tab;
  } else if (matchActivity) {
    setIds({
      ventId: paramsActivity.ventId,
      activityId: paramsActivity.activityId,
    });
    tab = paramsActivity.tab;
  } else if (matchCustomField) {
    setIds({
      ventId: paramsCustomField.ventId,
      customFieldId: paramsCustomField.customFieldId,
    });
    tab = paramsCustomField.tab;
  } else if (matchEmailTemplate) {
    setIds({
      ventId: paramsEmailTemplate.ventId,
      emailTemplateId: paramsEmailTemplate.emailTemplateId,
    });
    tab = paramsEmailTemplate.tab;
  } else if (matchUser) {
    setIds({
      userId: paramsUser.userId,
    });
    tab = paramsUser.tab;
  } else {
    tab = "guestevents";
  }

  // Scroll to top when tab changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);

  const [eventTabDescription, setEventTabDescription] = useState("");
  const [activityTabDescription, setActivityTabDescription] = useState("");
  const [customFieldTabDescription, setCustomFieldTabDescription] =
    useState("");
  const [guestTabDescription, setGuestTabDescription] = useState("");
  const [emailTemplateTabDescription, setEmailTemplateTabDescription] =
    useState("");
  const [userTabDescription, setUserTabDescription] = useState("");
  const [location, navigate] = useLocation();

  // If we have a ventId, check to see if the user is a guest so we can display the RSVP page link.
  // To do that we need to get the guest ID.
  const { data: getUsersGuestIdResponse } = useGetUsersGuestIdForVentQuery(
    {
      ventId: currentVentId!,
    },
    {
      skip:
        currentVentId == null ||
        authContext?.user == null ||
        (currentGuestId != null && currentGuestId != ""),
    },
  );

  if (getUsersGuestIdResponse && getUsersGuestIdResponse.guestId !== "") {
    setIds({ guestId: getUsersGuestIdResponse.guestId });
  }

  // Get the event so we can determine which tabs to display.
  // If the user is just a guest, don't show the event configuration tab.
  // If the user is not a guest and hot a host for the event, then server will log an error because the user won't have permission to get the event.
  // But we won't report the error.
  // We also get the custom field type if we have a custom field so we can display the correct tab.
  const { data: ventResponse, isError: isErrorFetchingVent } =
    useGetVentDisplayInfoQuery(
      {
        id: currentVentId!,
        customFieldId: currentCustomFieldId,
      },
      { skip: currentVentId == null || authContext?.user == null },
    );

  if (isErrorFetchingVent) {
    // Something went wrong, such as the ventId in the URL not matching any vent.
    // Get rid of all ID state so we can display the console home page.
    setTimeout(() => {
      setIds({
        ventId: null,
      });
    }, 0);
  }

  return (
    <AppShell
      header={{
        height: 60,
      }}
      navbar={{
        width: 150,
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <Modal
        size="auto"
        opened={openedPassword}
        onClose={closePassword}
        withCloseButton={false}
        centered
      >
        <Password onClose={closePassword} />
      </Modal>
      <AppShell.Header
        style={{ width: "100%", maxWidth: "100vw", boxSizing: "border-box" }}
      >
        <Flex gap="md" justify="space-between" align="center" direction="row">
          <Group>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
            />
            <ActionIcon
              style={{
                padding: 0,
                margin: 0,
                width: 50,
                height: 50,
                marginLeft: 10,
              }}
              variant="transparent"
              aria-label="Go home"
              onClick={(event) => {
                event.preventDefault();
                navigate("/");
              }}
            >
              <Logo />
            </ActionIcon>
            <Text>Early Bird RSVP</Text>
          </Group>
          <Flex justify="flex-end" gap="md" align="center">
            <Anchor
              href="https://www.earlybirdrsvp.com/support"
              size="sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              Help
            </Anchor>
            {authContext.user != null && (
              <Link to="/logoff" style={{ textDecoration: "none" }}>
                <Anchor component="span" underline="hover">
                  Logout
                </Anchor>
              </Link>
            )}
            {authContext.user == null && (
              <Link
                to="/login"
                style={{ textDecoration: "none" }}
                onClick={() =>
                  window.localStorage.setItem("intendedUrl", location)
                }
              >
                <Anchor component="span" underline="hover">
                  Login
                </Anchor>
              </Link>
            )}
            <Menu shadow="md" width={200} disabled={authContext.user == null}>
              <Menu.Target>
                <Avatar
                  color="initials"
                  mr="xs"
                  radius="xl"
                  name={formatInitials(
                    authContext.userAttributes?.firstName,
                    authContext.userAttributes?.lastName,
                  )}
                />
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item>{authContext.user?.email}</Menu.Item>
                <Menu.Item
                  onClick={() => {
                    navigate(getLinkToSettings());
                  }}
                  leftSection={<IconUserSettings />}
                >
                  Settings
                </Menu.Item>
                <Menu.Item
                  onClick={openPassword}
                  leftSection={<IconPassword />}
                >
                  Password
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Flex>
      </AppShell.Header>
      <AppShell.Navbar>
        {authContext.user && (
          <NavLink
            onClick={closeIfMobile}
            component={Link}
            label="Events as Guest"
            leftSection={<IconEventsAsGuest />}
            active={tab === "guestevents"}
            href={`/console/guestevents`}
          />
        )}
        {authContext.user &&
          authContext.userAttributes &&
          authContext.userAttributes.canOwnVents && (
            <NavLink
              onClick={closeIfMobile}
              component={Link}
              label="Events as Host"
              leftSection={<IconEventsAsHost />}
              active={tab === "hostevents"}
              href={`/console/hostevents`}
            />
          )}
        {currentVentId && (
          <NavLink
            onClick={closeIfMobile}
            component={Link}
            label="Event Home"
            leftSection={<IconEventHome />}
            description={eventTabDescription}
            active={tab === "eventhome"}
            href={`/console/eventhome/event/${currentVentId}`}
          />
        )}
        {currentVentId && ventResponse && ventResponse.canUserViewConfig && (
          <NavLink
            onClick={closeIfMobile}
            component={Link}
            label="Configuration"
            leftSection={<IconConfigureEvent />}
            active={tab === "eventconfig"}
            href={`/console/eventconfig/event/${currentVentId}`}
          />
        )}
        {currentVentId &&
          ventResponse &&
          authContext.user &&
          (ventResponse.canUserViewConfig ||
            (ventResponse.activitiesExist &&
              ventResponse.isUserGuestForVent)) && (
            <NavLink
              onClick={closeIfMobile}
              component={Link}
              label="Activities"
              leftSection={<IconActivities />}
              active={tab === "eventactivities"}
              href={`/console/eventactivities/event/${currentVentId}`}
            />
          )}
        {currentVentId && currentActivityId && (
          <NavLink
            onClick={closeIfMobile}
            component={Link}
            label={"Activity"}
            leftSection={<IconActivity />}
            description={activityTabDescription.substring(0, 15)}
            active={tab === "eventactivity"}
            href={`/console/eventactivity/event/${currentVentId}/activity/${currentActivityId}`}
          />
        )}
        {currentVentId && ventResponse && ventResponse.canUserViewConfig && (
          <NavLink
            onClick={closeIfMobile}
            component={Link}
            label="Custom Fields"
            leftSection={<IconCustomField />}
            active={tab === "customfields"}
            href={`/console/customfields/event/${currentVentId}`}
          />
        )}
        {currentVentId && currentCustomFieldId && (
          <NavLink
            onClick={closeIfMobile}
            component={Link}
            label={"Custom Field"}
            leftSection={<IconCustomField />}
            description={customFieldTabDescription.substring(0, 15)}
            active={tab === "customField"}
            href={`/console/customfield/event/${currentVentId}/customfield/${currentCustomFieldId}`}
          />
        )}
        {currentVentId &&
          authContext.user &&
          ventResponse &&
          ventResponse.canUserViewConfig && (
            <NavLink
              onClick={closeIfMobile}
              component={Link}
              label="Email Templates"
              leftSection={<IconEmailConfiguration />}
              active={tab === "emailTemplates"}
              href={`/console/emailTemplates/event/${currentVentId}`}
            />
          )}
        {currentVentId && currentEmailTemplateId && (
          <NavLink
            onClick={closeIfMobile}
            component={Link}
            label="Email Template"
            leftSection={<IconEmailTemplate />}
            description={emailTemplateTabDescription}
            active={tab === "emailTemplate"}
            href={`/console/emailTemplate/event/${currentVentId}/emailTemplate/${currentEmailTemplateId}`}
          />
        )}
        {currentVentId &&
          authContext.user &&
          ventResponse &&
          ventResponse.canUserViewGuests && (
            <NavLink
              onClick={closeIfMobile}
              component={Link}
              label="Guests"
              leftSection={<IconGuests />}
              active={tab === "guests"}
              href={`/console/guests/event/${currentVentId}`}
            />
          )}
        {currentGuestId && (
          <NavLink
            onClick={closeIfMobile}
            component={Link}
            label={"RSVP"}
            leftSection={<IconRsvp />}
            description={guestTabDescription}
            active={tab === "guest"}
            href={`/console/guest/event/${currentVentId}/guest/${currentGuestId}`}
          />
        )}
        <NavLink
          onClick={closeIfMobile}
          component={Link}
          label="Settings"
          active={tab === "settings"}
          leftSection={<IconUserSettings />}
          href={`/console/settings`}
        />
        {authContext.user &&
          authContext.userAttributes &&
          authContext.userAttributes.isAdmin && (
            <NavLink
              onClick={closeIfMobile}
              component={Link}
              label="Users"
              active={tab === "users"}
              href={`/console/users`}
            />
          )}
        {authContext.user &&
          authContext.userAttributes &&
          authContext.userAttributes.isAdmin &&
          currentUserId && (
            <NavLink
              onClick={closeIfMobile}
              component={Link}
              label="User"
              description={userTabDescription}
              active={tab === "user"}
              href={`/console/user/userid/${currentUserId}`}
            />
          )}
        {authContext.user &&
          authContext.userAttributes &&
          authContext.userAttributes.isAdmin && (
            <NavLink
              onClick={closeIfMobile}
              component={Link}
              label="Utilities"
              active={tab === "utilities"}
              href={`/console/utilities`}
            />
          )}
      </AppShell.Navbar>
      {tab === "eventhome" && currentVentId && (
        <VentHomeTab
          ventId={currentVentId}
          setTabDescription={setEventTabDescription}
        />
      )}
      {tab === "guestevents" && <VentsGuestTab />}
      {tab === "hostevents" && <VentsTab />}
      {tab === "eventconfig" && currentVentId && (
        <VentConfigTab
          ventId={currentVentId}
          setTabDescription={setEventTabDescription}
        />
      )}
      {tab === "guests" && currentVentId && (
        <GuestsTab
          ventId={currentVentId}
          setEventTabDescription={setEventTabDescription}
        />
      )}
      {tab === "guest" && currentVentId && currentGuestId && (
        <GuestTab
          ventId={currentVentId}
          guestId={currentGuestId}
          setTabDescription={setGuestTabDescription}
          setEventTabDescription={setEventTabDescription}
        />
      )}
      {tab === "eventactivities" && currentVentId && (
        <ActivitiesTab
          setEventTabDescription={setEventTabDescription}
          ventId={currentVentId}
        />
      )}
      {tab === "eventactivity" && currentVentId && currentActivityId && (
        <ActivityTab
          ventId={currentVentId}
          activityId={currentActivityId}
          setTabDescription={setActivityTabDescription}
          setEventTabDescription={setEventTabDescription}
        />
      )}
      {tab === "customfields" && currentVentId && (
        <CustomFieldsTab
          setEventTabDescription={setEventTabDescription}
          ventId={currentVentId}
        />
      )}
      {/* Need to decide on the custom field tab based on the custom field type */}
      {tab === "customfield" &&
        currentVentId &&
        currentCustomFieldId &&
        ventResponse?.customFieldType === "t" && (
          <CustomTextFieldTab
            ventId={currentVentId}
            customFieldId={currentCustomFieldId}
            setTabDescription={setCustomFieldTabDescription}
            setEventTabDescription={setEventTabDescription}
          />
        )}
      {tab === "customfield" &&
        currentVentId &&
        currentCustomFieldId &&
        ventResponse?.customFieldType === "o" && (
          <CustomOptionsFieldTab
            ventId={currentVentId}
            customFieldId={currentCustomFieldId}
            setTabDescription={setCustomFieldTabDescription}
            setEventTabDescription={setEventTabDescription}
          />
        )}
      {tab === "emailTemplates" && currentVentId && (
        <EmailTemplatesTab
          ventId={currentVentId}
          setEventTabDescription={setEventTabDescription}
        />
      )}
      {tab === "emailTemplate" && currentEmailTemplateId && currentVentId && (
        <EmailTemplateTab
          emailTemplateId={currentEmailTemplateId}
          ventId={currentVentId}
          setTabDescription={setEmailTemplateTabDescription}
          setEventTabDescription={setEventTabDescription}
        />
      )}
      {tab === "settings" && <UserAttributesTab />}
      {tab === "users" && <UsersTab />}
      {tab === "user" && currentUserId && (
        <UserTab
          setTabDescription={setUserTabDescription}
          userId={currentUserId}
        />
      )}
      {tab === "utilities" && <UtilitiesTab />}
    </AppShell>
  );
};

export default Console;
